/**
 * ImageUpload
 * Component for uploading and displaying profile images
 */

import { Avatar, AvatarFallback, AvatarImage, AvatarLoading } from '@shared/components/ui/avatar'
import { Button } from '@shared/components/ui/button'
import { Camera, Loader2, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

// ============================================================================
// Types
// ============================================================================

interface ImageUploadProps {
  currentImageUrl?: string
  onImageChange: (file: File | null) => void
  fallbackText?: string
  disabled?: boolean
  isUploading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function ImageUpload({
  currentImageUrl,
  onImageChange,
  fallbackText = '?',
  disabled = false,
  isUploading = false,
}: ImageUploadProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null)
  const [imageLoading, setImageLoading] = useState(false)
  const [imageError, setImageError] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)

  // Update preview URL when currentImageUrl changes
  useEffect(() => {
    if (currentImageUrl) {
      setPreviewUrl(currentImageUrl)
      setImageLoading(true)
      setImageError(false)
    } else {
      setPreviewUrl(null)
      setImageLoading(false)
      setImageError(false)
    }
  }, [currentImageUrl])

  // Handle image load success
  const handleImageLoad = () => {
    setImageLoading(false)
    setImageError(false)
  }

  // Handle image load error
  const handleImageError = () => {
    setImageLoading(false)
    setImageError(true)
  }

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen válido')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen no puede superar los 5MB')
      return
    }

    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string)
    }
    reader.readAsDataURL(file)

    // Notify parent
    onImageChange(file)
  }

  // Handle remove image
  const handleRemove = () => {
    setPreviewUrl(null)
    onImageChange(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Handle click on avatar to open file selector
  const handleAvatarClick = () => {
    if (!disabled) {
      fileInputRef.current?.click()
    }
  }

  // Handle camera button click
  const handleCameraClick = () => {
    if (!disabled) {
      cameraInputRef.current?.click()
    }
  }

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Avatar Preview */}
      <div className="relative group">
        <Avatar
          className="h-32 w-32 cursor-pointer border-4 border-background shadow-lg transition-all group-hover:shadow-xl"
          onClick={handleAvatarClick}
        >
          <AvatarImage
            src={previewUrl || undefined}
            alt="Profile"
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
          <AvatarFallback className="text-3xl">{fallbackText}</AvatarFallback>
          <AvatarLoading isLoading={(imageLoading || isUploading) && !imageError} />
        </Avatar>

        {/* Overlay with camera icon */}
        {!disabled && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            onClick={handleAvatarClick}
          >
            {isUploading ? (
              <Loader2 className="h-8 w-8 text-white animate-spin" />
            ) : (
              <Camera className="h-8 w-8 text-white" />
            )}
          </div>
        )}

        {/* Remove button */}
        {previewUrl && !disabled && (
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -top-2 -right-2 h-8 w-8 rounded-full shadow-lg"
            onClick={handleRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      {/* Hidden camera input */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      {/* Upload buttons */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleAvatarClick}
          disabled={disabled || isUploading}
        >
          <Camera className="h-4 w-4 mr-2" />
          {previewUrl ? 'Cambiar Foto' : 'Subir Foto'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleCameraClick}
          disabled={disabled || isUploading}
        >
          <Camera className="h-4 w-4 mr-2" />
          Tomar Foto
        </Button>
      </div>

      {/* Helper text */}
      <p className="text-xs text-muted-foreground text-center">
        Formatos: JPG, PNG, GIF (Máx. 5MB)
      </p>
    </div>
  )
}
